<template>
  <div :class="{ maxWindow: maxWindow }" class="editor-container" @mousedown.stop="" @dblclick.stop="handleMaxWindow">
    <div v-if="showBar" class="top-bar">
      <div>{{ title ? title : (maxWindow ? '编辑' : '') }}</div>
      <span style="color: #0000003f; cursor: pointer; user-select: none; font-size: 14px;">双击{{ maxWindow ? '还原' : '最大化' }}</span>
      <div class="row center-a right-menu">
        <el-tooltip effect="dark" placement="bottom">
          <template #content>
            <div class="row">操作说明：</div>
            <div class="row">· 快捷键遵循SublimeText风格</div>
            <div class="row">· Ctrl+S 保存</div>
            <div class="row">· Ctrl+C 复制</div>
            <div class="row">· Ctrl+V 粘贴</div>
            <div class="row">· Ctrl+F 查找</div>
            <div class="row">· Ctrl+D 多选</div>
          </template>
          <img src="@/assets/icons/help.png" class="icon" @click="$router.to('group')" />
        </el-tooltip>
        
        <el-tooltip effect="dark" :content="maxWindow?'还原':'最大化'" placement="bottom">
          <font-awesome-icon v-if="!maxWindow" class="icon-button" icon="expand-arrows-alt" title="最大化" @click="handleMaxWindow" />
          <font-awesome-icon v-else class="icon-button" icon="compress-arrows-alt" title="还原" @click="handleMaxWindow" />
        </el-tooltip>
        
        <el-tooltip v-if="showClose" effect="dark" content="关闭" placement="bottom">
          <font-awesome-icon class="icon-button" icon="close" title="关闭" @click="handleClose" />
        </el-tooltip>
      </div>
    </div>
    <Codemirror
      @keydown="save"
      @dblclick.stop=""
      class="code-mirror"
      :style="{ height: showBar ? 'calc(100% - 42px)' : '100%', width: '100%', maxWidth: '100vw' }"
      v-model="code"
      :options="cmOptions"
      :extensions="cmOptions.extensions"
      @change="codeChange"
    />
  </div>
</template>
<script>
import { Codemirror } from "vue-codemirror";

import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { java } from "@codemirror/lang-java";
import { sql } from "@codemirror/lang-sql";
import { vue } from "@codemirror/lang-vue";
import { html } from "@codemirror/lang-html";
import { markdown } from "@codemirror/lang-markdown";

import { darcula } from '@uiw/codemirror-theme-darcula';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';

function transform(mode) {
  const table = {
    'java': java(),
    'default': javascript(),
    'javascript': javascript(),
    'yml':  html(),
    'vue': vue(),
    "sql": sql(),
    "html": html(),
    'python': python(),
    "markdown": markdown()
  }
  return table[mode] ? table[mode] : table.default
}
function transformStyle(mode) {
  const table = {
    'java': darcula,
    'javascript': darcula,
    'vue': vscodeDark,
    'html': vscodeDark,
    'default': vscodeDark
  }
  return table[mode] ? table[mode] : table.default
}

export default {
  name: 'CodeEditor',
  props: {
    cmMode: {
      type: String,
      default: 'default'
    },
    modelValue: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: undefined
    },
    showBar: {
      type: Boolean,
      default: true
    },
    showClose: {
      type: Boolean,
      default: false
    },
  },
  components: {
    Codemirror
  },
  data() {
    return {
      editor: undefined,
      maxWindow: false,
      body: undefined,
      parent: undefined,
      code: undefined,
      originalIndex: -1,
      nextSibling: undefined,
      cmOptions: {
        extensions: [transform(this.cmMode), transformStyle(this.cmMode)],
        lineWrapping: true,
        lineNumbers: true,
        readOnly: false,
        autofocus: false,
        smartIndent: true,
        autocorrect: true,
        spellcheck: true,
        indentUnit: 2,
        keyMap: 'sublime',
        lint: true,
        hintOptions: {
          hint: this.handleShowHint,
          completeSingle: false
        },
        gutters: this.gutter ? [
          'CodeMirror-lint-markers',
          'CodeMirror-linenumbers',
          'CodeMirror-foldgutter'
        ] : [],
        foldGutter: true,
        autoCloseBrackets: true,
        autoCloseTags: true,
        matchTags: { bothTags: true },
        matchBrackets: true,
        styleActiveLine: true,
        autoRefresh: true,
        highlightSelectionMatches: {
          minChars: 2,
          style: 'matchhighlight',
          showToken: true
        },
        styleSelectedText: true,
        enableAutoFormatJson: this.autoFormatJson === null ? true : this.autoFormatJson,
        defaultJsonIndentation: !this.jsonIndentation || typeof this.jsonIndentation !== typeof 1 ? 2 : this.jsonIndentation,
        value: this.modelValue
      },
    };
  },
  watch: {
    cmMode: function() {
      this.cmOptions.mode = this.cmMode
      this.cmOptions.theme = transformStyle(this.cmMode)
    },
    modelValue(newValue) {
      if (this.code != newValue) {
        this.code = newValue
      }
    }
  },
  created() {
    this.code = this.modelValue
    this.cmOptions.value = this.modelValue
  },
  mounted() {
    this.parent = this.$el.parentElement
    this.body = document.getElementById('app')
    this.originalIndex = Array.from(this.parent.children).indexOf(this.$el);
    this.nextSibling = this.$el.nextSibling; // 记录下一个兄弟元素

    // this.$nextTick(() => {
    //   this.cmOptions.value = this.modelValue
    //   this.editor = CodeMirror(this.$refs.editor, this.cmOptions);
    //   this.editor.on('change', () => {
    //     this.$emit('update:modelValue', this.editor.getValue());
    //   });
    // });
  },
  methods: {
    handleClose() { this.$emit('close') },
    handleMaxWindow() {
      this.maxWindow = !this.maxWindow
      if (this.maxWindow) {
        this.parent.removeChild(this.$el);
        this.body.append(this.$el);
      } else {
        this.body.removeChild(this.$el);
        if (this.nextSibling) {
          this.parent.insertBefore(this.$el, this.nextSibling);
        } else {
          this.parent.appendChild(this.$el);
        }
      }
    },
    codeChange(e) {
      this.code = e
      this.$emit('update:modelValue', e)
      this.$emit('change', e)
    },
    save(event) {
      if (event.ctrlKey && event.key === 's') {
        event.preventDefault(); // 阻止默认行为
        // 在这里执行你的代码
        this.$emit('update:modelValue', this.code)
        this.$emit('change', this.code)
        this.$emit('save')
      }
    },
  }
};
</script>
<style lang="scss" scoped>
.editor-container {
  width: 100%;
  height: 100%;
  border: 1px solid #434343;
}
.top-bar {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 5px 14px;
  .right-menu {
    .icon {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
    .icon-button {
      cursor: pointer;
      margin-left: 10px;
      &:HOVER {
        color: #bbb;
      }
    }
  }
}
.maxWindow {
  // background: #FFFFFF;
  position: fixed;
  width: 100%;
  height: 100% !important;
  margin-top: 0 !important;
  top: 0;
  left: 0;
  z-index: 999999999;
  background: #292A2B;
  color: #FFFFFF;
  .top-bar {
    background: #FFFFFF;
    color: #292A2B;
  }
}
</style>
<style lang="css">
.code-mirror{
  font-size : 12px;
  line-height : 18px;
  min-height: 300px;
  height: 100%;
}

.maxWindow .code-mirror{
  height: calc(100vh - 29px);
}
.code-mirror * {
  font-family: Consolas !important;
  font-size : 12px;
  line-height : 18px;
}
</style>
